import React, { useContext, useCallback, useState } from 'react';
import { getPropertyDisplayLabel } from 'components/property-editors/utils/ProviderNodeUtils';
import { IEditorProps } from './Editor';
import { GridPopup } from 'components/property-editors/PropertyGrid/GridPopup';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { useEditorId } from './hooks/useEditorId';
import { getEditorDataAttributeProps } from './utils/EditorDataAttributeHelpers';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import classNames from 'classnames';

interface IEditorPopupProps<O extends PopupEditorOptions> extends IEditorProps {
  children?: React.ReactNode;
  className?: string;
  dialogOptions?: O;
}

export interface PopupEditorOptions {
  sourceElement?: Element;
  // these callbacks only make sense if we are updating local state within an editor via callback...
  // and then we have the problem of managing state vs props which is an anti-pattern... :(
  onOk?: (result: any) => void;
  onClosed?: () => void;
  onClear?: () => void;
  editingContextId?: string;
  placeholder?: string;
}

/**
 * Popup Buttons for a ContextProviderNode.
 *
 * This is a generic component. You probably mean to wrap it in a specific editor to open a specific dialog.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function EditorPopup<O extends PopupEditorOptions>({
  child,
  className,
  dialogOptions,
  path,
  children,
  hideInputLabel,
}: IEditorPopupProps<O>) {
  const context = useContext(PropertyEditorContext);
  const [testId] = useEditorId(path);
  const validation = getEditorValidationSummary(child);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const dataAttrs = getEditorDataAttributeProps(child, path);
  // explicity check for false, in case it's not defined, e.g. ColorPicker doesn't define it.
  // should we just make that server-side change?
  const canPick = getEditorConfigValue(child, 'CanBePicked') !== false;
  const onClearClick = useCallback(() => {
    dialogOptions?.onClear?.();

    context.onChange(null, path);
  }, [context, path, dialogOptions]);

  const getDialogOptions = useCallback(
    (e: React.MouseEvent<Element>) => {
      return {
        ...dialogOptions,
        onClear: () => {
          context.onChange(null, path);
          dialogOptions?.onClear?.();
          setDialogOpen(false);
        },
        onClosed: () => {
          dialogOptions?.onClosed?.();
          setDialogOpen(false);
        },
        sourceElement: e.currentTarget,
        editingContextId: context.editingContextId,
      } as O;
    },
    [context, dialogOptions, path]
  );

  const onOpen = useCallback(
    (e: React.MouseEvent<Element>) => {
      setDialogOpen(true);
      context.onShowDialogue({
        node: child,
        editingContextId: context.editingContextId,
        path,
        options: getDialogOptions(e),
        onDone: () => {
          setDialogOpen(false);
        },
      });
    },
    [child, context, getDialogOptions, path]
  );

  const id = path.join('.') + getPropertyDisplayLabel(child);
  let value = child?.Value?.OutputValue;
  value = typeof value === 'string' ? value : undefined;
  // these are sometimes complex types! TODO handle
  return (
    <GridPopup
      id={id}
      className={classNames(className, child.className)}
      label={getPropertyDisplayLabel(child)}
      value={value}
      validation={validation}
      onClear={onClearClick}
      onOpen={onOpen}
      data-testid={testId}
      disableOpen={!canPick || dialogOpen}
      disableClear={!canPick || dialogOpen}
      placeholder={dialogOptions?.placeholder}
      hideLabel={hideInputLabel}
      {...dataAttrs}
    >
      {children}
    </GridPopup>
  );
}
